import { ErrorModal } from "@dexteel/mesf-core";
import { Grid2 as Grid, TextField, Typography } from "@mui/material";
import { Dispatch, SetStateAction, useState } from "react";
import { GradeSelector } from "../../../../../meltshop/month-report/components/selector/GradeSelector";
import { DeliveryTicket } from "../../../../models/DeliveryTicket";
import { useCastersContext } from "../../../CastersContext";
import { useSaveProcess } from "../../../hooks/useSaveProcess";
import { ProcessDataChanges } from "./DeliveryTicketModal";
import { MoldSizeSelector } from "./selector/MoldSizeSelector";
import { useDeliveryTicketStyles } from "./styles/deliveryTicketStyles";

interface Props {
	deliveryTicket: DeliveryTicket | null;
	handleHeatInputChange: (field: string, value: any) => void;
	dateTimeFormatted: any;
	errors: Map<string, string>;
	setErrors: Dispatch<SetStateAction<Map<string, string>>>;
	setPendingChanges: Dispatch<SetStateAction<ProcessDataChanges>>;
}

export const ProcessData = ({
	deliveryTicket,
	handleHeatInputChange,
	dateTimeFormatted,
	errors,
	setErrors,
	setPendingChanges,
}: Props) => {
	const { state, actions } = useCastersContext();
	const { saveProcessData } = useSaveProcess();
	const classes = useDeliveryTicketStyles();
	const [error, setError] = useState("");

	const [casterForeman, setCasterForeman] = useState<string | null>(
		deliveryTicket?.heatData.CasterForeman ?? null,
	);
	const [melter, setMelter] = useState<string | null>(
		deliveryTicket?.heatData.Melter ?? null,
	);
	const [labTech, setLabTech] = useState<string | null>(
		deliveryTicket?.heatData.LabTech ?? null,
	);
	const [sequence, setSequence] = useState<string | null>(
		deliveryTicket?.heatData.Sequence ?? null,
	);

	const handleChangeGrade = (gradeIdSelected: number | null) => {
		handleHeatInputChange("GradeId", gradeIdSelected);
		setPendingChanges((prev: ProcessDataChanges) => ({
			...prev,
			gradeId: gradeIdSelected,
		}));

		if (gradeIdSelected === null) {
			setErrors((prevErrors) => {
				const newErrors = new Map(prevErrors);
				newErrors.set("GradeId", "Please select a grade");
				return newErrors;
			});
		} else {
			setErrors((prevErrors) => {
				const newErrors = new Map(prevErrors);
				newErrors.delete("GradeId");
				return newErrors;
			});
		}
	};

	const handleChangeMoldSize = (moldSizeIdSelected: number | null) => {
		handleHeatInputChange("MoldSizeId", moldSizeIdSelected);
		setPendingChanges((prev: ProcessDataChanges) => ({
			...prev,
			moldSizeId: moldSizeIdSelected,
		}));
	};

	const handleCasterForemanChange = () => {
		handleHeatInputChange("CasterForeman", casterForeman);
		setPendingChanges((prev: ProcessDataChanges) => ({
			...prev,
			casterForeman,
		}));
	};

	const handleMelterChange = () => {
		handleHeatInputChange("Melter", melter);
		setPendingChanges((prev: ProcessDataChanges) => ({ ...prev, melter }));
	};

	const handleLabTechChange = () => {
		handleHeatInputChange("LabTech", labTech);
		setPendingChanges((prev: ProcessDataChanges) => ({ ...prev, labTech }));
	};

	const handleSequenceChange = () => {
		handleHeatInputChange("Sequence", sequence);
		setPendingChanges((prev: ProcessDataChanges) => ({ ...prev, sequence }));
	};
	return (
		<>
			<Grid
				container
				size={{ xs: 8 }}
				spacing={1}
				style={{ marginBottom: "30px" }}
			>
				<Grid container size={{ xs: 12 }}>
					<Grid size={{ xs: 6 }} alignContent="center">
						<Typography style={{ fontWeight: "bold" }}>HEAT No:</Typography>
					</Grid>
					<Grid size={{ xs: 6 }} alignContent="center">
						<TextField
							value={deliveryTicket?.heatData.HeatName ?? ""}
							onChange={(e) =>
								handleHeatInputChange("HeatName", e.target.value)
							}
							fullWidth
							variant="outlined"
							size="small"
							slotProps={{
								input: {
									readOnly: true,
								},
							}}
							className={classes.disabledInput + " " + classes.input}
						/>
					</Grid>
				</Grid>

				<Grid container size={{ xs: 12 }}>
					<Grid size={{ xs: 6 }} alignContent="center">
						<Typography style={{ fontWeight: "bold" }}>GRADE:</Typography>
					</Grid>
					<Grid size={{ xs: 6 }} alignContent="center">
						<GradeSelector
							value={deliveryTicket?.heatData.GradeId ?? null}
							initialValue={{ GradeId: null, GradeName: "" }}
							onChange={(value) => {
								handleChangeGrade(value);
							}}
							className={classes.input}
							inputError={errors.get("GradeId")}
						/>
					</Grid>
				</Grid>

				<Grid container size={{ xs: 12 }}>
					<Grid size={{ xs: 6 }} alignContent="center">
						<Typography style={{ fontWeight: "bold" }}>MOLD SIZE:</Typography>
					</Grid>
					<Grid size={{ xs: 6 }} alignContent="center">
						<MoldSizeSelector
							value={deliveryTicket?.heatData.MoldSizeId ?? null}
							onChange={(value) => {
								handleChangeMoldSize(value);
							}}
							className={classes.input}
						/>
					</Grid>
				</Grid>

				<Grid container size={{ xs: 12 }}>
					<Grid size={{ xs: 6 }} alignContent="center">
						<Typography style={{ fontWeight: "bold" }}>
							CASTER FOREMAN:
						</Typography>
					</Grid>
					<Grid size={{ xs: 6 }} alignContent="center">
						<TextField
							value={casterForeman}
							onBlur={(e) => handleCasterForemanChange()}
							onChange={(e) => setCasterForeman(e.target.value)}
							fullWidth
							variant="outlined"
							size="small"
							className={classes.input}
						/>
					</Grid>
				</Grid>

				<Grid container size={{ xs: 12 }}>
					<Grid size={{ xs: 6 }} alignContent="center">
						<Typography style={{ fontWeight: "bold" }}>CAST DATE:</Typography>
					</Grid>
					<Grid size={{ xs: 6 }} alignContent="center">
						<TextField
							type="date"
							value={dateTimeFormatted?.castDateFormatted ?? ""}
							onChange={(e) =>
								handleHeatInputChange("CastDate", e.target.value)
							}
							slotProps={{
								input: {
									readOnly: true,
								},
								inputLabel: {
									shrink: true,
								},
							}}
							fullWidth
							variant="outlined"
							size="small"
							className={classes.disabledInput + " " + classes.input}
						/>
					</Grid>
				</Grid>

				<Grid container size={{ xs: 12 }}>
					<Grid size={{ xs: 6 }} alignContent="center">
						<Typography style={{ fontWeight: "bold" }}>TAP TIME:</Typography>
					</Grid>
					<Grid size={{ xs: 6 }} alignContent="center">
						<TextField
							type="time"
							value={dateTimeFormatted?.tapTime ?? ""}
							onChange={(e) => handleHeatInputChange("TapTime", e.target.value)}
							slotProps={{
								input: {
									readOnly: true,
								},
								inputLabel: {
									shrink: true,
								},
							}}
							fullWidth
							variant="outlined"
							size="small"
							className={classes.disabledInput + " " + classes.input}
						/>
					</Grid>
				</Grid>

				<Grid container size={{ xs: 12 }}>
					<Grid size={{ xs: 6 }} alignContent="center">
						<Typography style={{ fontWeight: "bold" }}>MELTER:</Typography>
					</Grid>
					<Grid size={{ xs: 6 }} alignContent="center">
						<TextField
							value={melter}
							onChange={(e) => setMelter(e.target.value)}
							onBlur={(e) => {
								handleMelterChange();
							}}
							fullWidth
							variant="outlined"
							size="small"
							className={classes.input}
						/>
					</Grid>
				</Grid>

				<Grid container size={{ xs: 12 }}>
					<Grid size={{ xs: 6 }} alignContent="center">
						<Typography style={{ fontWeight: "bold" }}>LAB TECH:</Typography>
					</Grid>
					<Grid size={{ xs: 6 }} alignContent="center">
						<TextField
							value={labTech}
							onChange={(e) => setLabTech(e.target.value)}
							onBlur={(e) => {
								handleLabTechChange();
							}}
							fullWidth
							variant="outlined"
							size="small"
							className={classes.input}
						/>
					</Grid>
				</Grid>
				<Grid container size={{ xs: 12 }}>
					<Grid size={{ xs: 6 }} alignContent="center">
						<Typography style={{ fontWeight: "bold" }}>SEQUENCE:</Typography>
					</Grid>
					<Grid size={{ xs: 6 }} alignContent="center">
						<TextField
							value={sequence}
							onChange={(e) => setSequence(e.target.value)}
							onBlur={(e) => {
								handleSequenceChange();
							}}
							fullWidth
							variant="outlined"
							size="small"
							className={classes.input}
						/>
					</Grid>
				</Grid>
			</Grid>
			<ErrorModal
				error={error || state.errorMessage}
				onHide={() => {
					if (error) setError("");
					if (state.errorMessage) actions.setErrorMessage("");
				}}
			/>
		</>
	);
};
